import React, { useState, useEffect } from "react";
import { Users, User } from "lucide-react";
import { X } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

import Modal from "react-modal";
import { useGroupJoining } from "../hooks/useGroupJoining";
import PlansModal from "./PlansModal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
Modal.setAppElement("#root");

const ExploreGroupDetailsModal = ({ group, onClose, onJoinClick }) => {
  const { user, setUser } = useAuth(); // Destructure setUser to update context

  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = async () => {
    try {
      // Fetch classes
      if (group.classIds && group.classIds?.length > 0) {
        const classesData = await Promise.all(
          group.classIds.map(async (classId) => {
            const classDoc = await getDoc(doc(db, "classes", classId));
            return classDoc.exists()
              ? { id: classDoc.id, ...classDoc.data() }
              : null;
          }),
        );
        setClasses(classesData.filter(Boolean));
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [group]);

  const renderClasses = () => {
    if (classes.length === 0) {
      return (
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500">No classes available</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {classes.map((classItem) => (
          <div key={classItem.id} className="rounded-lg bg-white p-4 shadow">
            <div className="relative mb-4 h-48 w-full overflow-hidden rounded-lg">
              <img
                src={classItem.imageUrl || "/images/panda.png"}
                alt={classItem.className}
                className="h-full w-full object-cover"
              />
            </div>
            <h3 className="mb-2 text-xl font-medium">{classItem.className}</h3>
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                {group.groupLearningLanguage}
              </span>
              <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                Advanced
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src={classItem.teacherImageUrl || "/images/panda.png"}
                  alt={classItem.teacherName}
                  className="h-6 w-6 rounded-full"
                />
                <span className="text-sm text-gray-600">
                  {classItem.teacherName} (Admin)
                </span>
              </div>
              <span className="text-sm text-gray-600">
                {classItem.maxStudents || 100}/100
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
        <div className="mx-4 w-full max-w-5xl rounded-3xl bg-white p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Group Details</h2>
            <button
              onClick={onClose}
              className="rounded-full p-2 hover:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex gap-6">
            {/* Left sidebar */}
            <div className="w-1/3 rounded-2xl bg-[#fffef0] p-6">
              <div className="flex flex-col items-center text-center">
                <img
                  src={group.imageUrl}
                  alt={group.groupName}
                  className="mb-4 h-32 w-32 rounded-full"
                />
                <h3 className="mb-2 text-2xl font-medium">{group.groupName}</h3>
                <div className="mb-2 flex items-center gap-2">
                  <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                    {group.groupLearningLanguage}
                  </span>
                  <span className="rounded-full bg-yellow-200 px-3 py-1 text-sm">
                    Advanced
                  </span>
                </div>
                <div className="mb-4 flex items-center gap-2">
                  <img
                    src={group.groupAdminImageUrl || "/images/panda.png"}
                    alt={group.groupAdminName}
                    className="h-6 w-6 rounded-full"
                  />
                  <span className="text-sm">
                    {group.groupAdminName} (Admin)
                  </span>
                </div>
                <p className="mb-6 text-gray-600">{group.groupDescription}</p>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1">
              {loading ? (
                <div className="flex h-64 items-center justify-center">
                  <ClipLoader color="#FFB800" size={40} />
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  {renderClasses()}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <button
              className="mt-2 w-40 rounded-full border border-black py-2 font-medium text-black"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="mt-2 w-40 rounded-full border border-black bg-[#ffbf00] py-2 font-medium text-black hover:bg-[#ffbf00]"
              onClick={onJoinClick}
            >
              Join
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const ProfileSetupGroupCard = ({ group, onJoin, isJoined, setIsProcessingBooking }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showJoinConfirmation, setShowJoinConfirmation] = useState(false);

  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    groupName,
    groupLearningLanguage,
    level = "",
    groupAdminName = "Admin",
    memberIds = [],
    imageUrl,
    isPremium,
    groupAdminImageUrl,
    groupDescription,
    id: groupId,
  } = group;

  // Sync user context with session storage on mount and when user changes
  // This ensures that if free trial was activated in another view (like classes),
  // the updated freeAccess flag is reflected here
  useEffect(() => {
    const syncUserFromSession = () => {
      try {
        const sessionUser = sessionStorage.getItem("user");
        if (sessionUser && user?.uid) {
          const parsedUser = JSON.parse(sessionUser);
          // Only update if session storage has freeAccess but context doesn't
          // and they're the same user (to avoid race conditions)
          if (parsedUser.uid === user.uid && parsedUser.freeAccess && !user.freeAccess) {
            console.log("Syncing freeAccess from session storage to context in group card");
            setUser(parsedUser);
          }
        }
      } catch (error) {
        console.error("Error syncing user from session storage:", error);
      }
    };

    syncUserFromSession();
  }, [user?.uid, user?.freeAccess, setUser]); // Check when user changes

  const checkValidSubscription = () => {
    // Check freeAccess flag first
    if (user?.freeAccess) {
      return true;
    }

    // Check if user has any subscriptions
    if (!user.subscriptions || user.subscriptions.length === 0) {
      return false;
    }

    // Find a valid subscription that grants premium group access
    return user.subscriptions.some((sub) => {
      if (
        !sub.startDate ||
        !sub.endDate ||
        sub.type === "None" ||
        sub.type === "none"
      ) {
        return false;
      }
      const endDate = new Date(sub.endDate.seconds * 1000);
      const startDate = new Date(sub.startDate.seconds * 1000);
      const now = new Date();

      // Check if subscription is active
      if (endDate <= now || startDate > now) {
        return false;
      }

      const type = sub.type.trim().toLowerCase();
      return (
        type === "bammbuu groups" ||
        type === "immersive exam prep" ||
        type === "bammbuu+ instructor-led group classes" ||
        type === "group_premium"
      );
    });
  };

  const handlePremiumAccess = () => {
    if (!isPremium) {
      return true;
    }

    const hasValidSubscription = checkValidSubscription();
    if (!hasValidSubscription) {
      navigate("/plans");
      return false;
    }

    return true;
  };

  const handleJoinClick = (e) => {
    e.stopPropagation(); // Prevent triggering the card click
    setShowJoinConfirmation(true);
  };

  const updateContextAndSession = (newGroupId) => {
    // Create updated user object with new group
    const updatedUser = {
      ...user,
      joinedGroups: [...(user.joinedGroups || []), newGroupId],
    };

    // Update context
    setUser(updatedUser);

    // Update session storage
    const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const updatedStoredUser = {
      ...storedUser,
      joinedGroups: [...(storedUser.joinedGroups || []), newGroupId],
    };
    sessionStorage.setItem("user", JSON.stringify(updatedStoredUser));
  };

  const getLanguageFlag = (language) => {
    if (language === "English") return "/svgs/xs-us.svg";
    if (language === "Spanish") return "/svgs/xs-spain.svg";
    return "/svgs/eng-spanish-xs.svg";
  };

  const getLanguageEmoji = (language) => {
    if (language === "English") return "🇺🇸";
    if (language === "Spanish") return "🇪🇸";
    return "🇺🇸🇪🇸";
  };

  const { joinGroup, isProcessing } = useGroupJoining();
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);

  const handleJoinConfirm = async () => {
    // Prevent multiple clicks
    if (isProcessing) {
      return;
    }

    try {
      const success = await joinGroup(
        user,
        { groupId, isPremium },
        updateContextAndSession,
        () => {
          // Success callback
          setShowJoinConfirmation(false);
          setShowDetailsModal(false);
          if (onJoin) {
            onJoin(groupId);
          }
        },
        (errorMessage) => {
          // Error callback
          if (
            errorMessage.includes("subscription") ||
            errorMessage.includes("plan")
          ) {
            setShowJoinConfirmation(false);
            setIsPlansModalOpen(true);
          }
        },
      );

      if (!success) {
        // Handle cancellation or failure
        return;
      }
    } catch (error) {
      console.error("Error joining group:", error);
      // Handle error appropriately - show error message to user
    }
  };

  // Callback to retry joining after trial activation
  const handleTrialActivatedAndJoin = async () => {
    // Set processing state immediately for seamless transition
    if (setIsProcessingBooking) {
      setIsProcessingBooking(true);
    }
    
    try {
      // Fetch the updated user data from Firestore to ensure we have freeAccess
      const userRef = doc(db, "students", user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        toast.error("Failed to verify account. Please try again.");
        if (setIsProcessingBooking) {
          setIsProcessingBooking(false);
        }
        return;
      }
      
      const freshUserData = userDoc.data();
      const updatedUser = {
        ...user,
        ...freshUserData,
        freeAccess: freshUserData.freeAccess,
        subscriptions: freshUserData.subscriptions || []
      };
      
      // Update context with fresh user data
      setUser(updatedUser);
      
      // Update session storage with complete user data including freeAccess
      sessionStorage.setItem("user", JSON.stringify(updatedUser));
      
      // Minimal delay to ensure context propagates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const success = await joinGroup(
        updatedUser,
        { groupId, isPremium },
        updateContextAndSession,
        () => {
          // Success callback
          if (onJoin) {
            onJoin(groupId);
          }
        },
        (errorMessage) => {
          console.error("Join error:", errorMessage);
          toast.error("Failed to join group. Please try again.");
        },
      );
      
      if (success) {
        // Show success message after everything is complete
        toast.success("All set! Your group has been joined successfully!");
      }
    } catch (error) {
      console.error("Error joining group after trial:", error);
      toast.error("Failed to join group. Please try again.");
    } finally {
      // Clear processing state
      if (setIsProcessingBooking) {
        setIsProcessingBooking(false);
      }
    }
  };

  const handleCancel = () => {
    setShowJoinConfirmation(false);
  };

  return (
    <>
      <div className="w-80 hover:cursor-pointer">
        <div
          className={`flex h-full flex-col rounded-3xl p-4 ${
            isPremium
              ? "border border-[#14b82c] bg-[#e8feeb]"
              : "border border-[#ffc310] bg-white"
          }`}
        >
          {/* Rest of your existing GroupCard code */}
          <div className="flex h-full flex-col items-center">
            {/* Tag moved to top right */}
            <div className="relative mb-4 h-40 w-40">
              {/* Community Free / Tutor-Led Tag - Top Right */}
              <div className="absolute -right-[4.5rem] top-2 z-10">
                <span
                  className={`rounded-full px-3 py-2 text-sm font-medium ${
                    isPremium
                      ? "bg-[#FFBF00] text-black"
                      : "bg-[#fff885] text-[#042f0c]"
                  }`}
                >
                  {isPremium
                    ? t("profileSetup.groupTags.tutorLedTrial", "Tutor-Led")
                    : t(
                        "profileSetup.groupTags.communityFree",
                        "Community (Free)",
                      )}
                </span>
              </div>

              <img
                src={imageUrl}
                alt={groupName}
                className="h-full w-full rounded-full object-cover"
              />
            </div>

            <h2 className="mb-2 text-2xl font-medium text-gray-900">
              {groupName}
            </h2>

            {/* Language display like classes */}
            <div className="mb-4 flex items-center space-x-2">
              <img
                src={getLanguageFlag(groupLearningLanguage)}
                alt={
                  groupLearningLanguage === "English" ? "US Flag" : "Spain Flag"
                }
                className="h-4 w-4"
              />
              <span className="text-[#042f0c]">{groupLearningLanguage}</span>
            </div>

            <div className="mb-4 flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
              <img
                src={groupAdminImageUrl || "/images/panda.png"}
                alt={groupAdminName}
                className="h-4 w-4 rounded-full"
              />
                <span className="text-md text-gray-700">
                  {groupAdminName}{" "}
                  <span className="text-gray-500">(Admin)</span>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4 text-gray-600" />
                <span className="text-md text-gray-700">
                  {memberIds.length} members
                </span>
              </div>
            </div>

            <div className="mt-auto w-full">
              <button
                className={`w-full rounded-full py-2 font-medium ${
                  isJoined
                    ? "cursor-default bg-[#14b82c] text-white"
                    : "border border-black bg-[#ffbf00] text-black hover:bg-[#ffbf00]"
                }`}
                onClick={isJoined ? undefined : handleJoinClick}
                disabled={isJoined}
              >
                {isJoined
                  ? isPremium
                    ? t("profileSetup.buttons.joinedTrial", "Joined")
                    : t("profileSetup.buttons.joinedFree", "Joined")
                  : isPremium
                    ? t("profileSetup.buttons.startTrial", "Start Trial")
                    : t("profileSetup.buttons.joinFree", "Join")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDetailsModal && (
        <ExploreGroupDetailsModal
          group={group}
          onClose={() => setShowDetailsModal(false)}
          onJoinClick={handleJoinClick}
        />
      )}
      <Modal
        isOpen={showJoinConfirmation}
        onRequestClose={handleCancel}
        className="z-50 mx-auto mt-40 max-w-sm rounded-3xl bg-white p-6 font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        style={{
          overlay: {
            zIndex: 60, // Higher than the details modal
          },
          content: {
            border: "none",
            padding: "24px",
            maxWidth: "420px",
            position: "relative", // Needed for z-index to work
            zIndex: 61, // Higher than the overlay
          },
        }}
      >
        <div className="text-center">
          <h2 className="mb-4 text-xl font-semibold">
            Are you sure you want to join this group?
          </h2>
          <div className="flex flex-row gap-2">
            <button
              className="w-full rounded-full border border-gray-300 py-2 font-medium hover:bg-gray-50"
              onClick={handleCancel}
              disabled={isProcessing}
            >
              No, Cancel
            </button>
            <button
              className={`w-full rounded-full border border-[#042f0c] py-2 font-medium text-black ${
                isProcessing
                  ? "cursor-not-allowed bg-gray-300"
                  : "bg-[#14b82c] hover:bg-[#119924]"
              }`}
              onClick={handleJoinConfirm}
              disabled={isProcessing}
            >
              {isProcessing ? "Joining...." : "Yes, Join"}
            </button>
          </div>
        </div>
      </Modal>
      <PlansModal
        isOpen={isPlansModalOpen}
        onClose={() => setIsPlansModalOpen(false)}
        initialTab="offers"
        onTrialActivated={handleTrialActivatedAndJoin}
        directFreeTrial={true}
      />
    </>
  );
};

export default ProfileSetupGroupCard;
